import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAirline } from '../../context/AirlineContext';
import { searchLogTs } from '../../services/logTService';
import { fetchLogTContentById, fetchAirlineByAirline } from '../../services/logTContentService';
import { ILogt } from '../../models/logT';
import { LogtSearchCriteria } from '../../models/logTSearchCriteria';
import { ILogTContent } from '../../models/logTContent';
import { formatZuluDateTimeLabel } from '../../utils/zuluTime';
import { generateCSV } from '../../appInsights';
import BetaExportButtons from './BetaExportButtons';
import OverviewCsvModal from '../OverviewCsvModal';
import BetaFilterBar, { ActiveFilters, BetaFilterBarHandle } from './BetaFilterBar';
import BetaLogTGrid from './BetaLogTGrid';
import BetaMessageDetail from './BetaMessageDetail';
import '../../styles/beta/BetaFilterBar.scss';
import '../../styles/beta/BetaDatePicker.scss';
import '../../styles/beta/BetaGrid.scss';
import '../../styles/beta/BetaMessageDetail.scss';
import '../../styles/beta/BetaToggle.scss';

interface BetaOverviewProps {
  isAdmin?: boolean;
  useExtendedSearchApi?: boolean;
}

const BetaOverview: React.FC<BetaOverviewProps> = ({ isAdmin = false, useExtendedSearchApi = false }) => {
  const { userData } = useAuth();
  const { airline } = useAirline();
  const filterBarRef = useRef<BetaFilterBarHandle>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<ILogt[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [hasMore, setHasMore] = useState(false);
  const [sortColumn, setSortColumn] = useState('logtTS');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [selectedRow, setSelectedRow] = useState<ILogt | null>(null);
  const [messageContent, setMessageContent] = useState<ILogTContent | null>(null);
  const [messageLoading, setMessageLoading] = useState(false);
  const [messageError, setMessageError] = useState(false);
  const [lastCriteria, setLastCriteria] = useState<LogtSearchCriteria | null>(null);
  const [aircraftList, setAircraftList] = useState<string[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters | null>(null);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvStartDate, setCsvStartDate] = useState<Date | null>(null);
  const [csvEndDate, setCsvEndDate] = useState<Date | null>(null);
  const [csvReportType, setCsvReportType] = useState<'accessed' | 'searched'>('accessed');
  const [isCSVLoading, setIsCSVLoading] = useState(false);

  const handleCancelSearch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
  }, []);

  const handleReset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setData([]);
    setTotalCount(0);
    setIsLoading(false);
    setPage(1);
    setHasMore(false);
    setSortColumn('logtTS');
    setSortDirection('DESC');
    setSelectedRow(null);
    setMessageContent(null);
    setMessageLoading(false);
    setMessageError(false);
    setLastCriteria(null);
    setLastRefreshed(null);
    setActiveFilters({
      tailNumbers: [],
      flightNumbers: [],
      messageTypes: [],
      keyword: '',
      smi: '',
      orig: '',
      dest: '',
    });
  }, []);

  const handleCsvDownload = useCallback(async () => {
    setIsCSVLoading(true);
    try {
      await generateCSV(csvStartDate, csvEndDate, csvReportType);
    } catch (err) {
      console.error('CSV download error:', err);
    } finally {
      setIsCSVLoading(false);
    }
  }, [csvStartDate, csvEndDate, csvReportType]);

  const accessToken = userData?.access_token || '';

  // Reset everything when airline changes (e.g. AS <-> QX)
  const prevAirlineRef = useRef(airline);
  useEffect(() => {
    if (prevAirlineRef.current !== airline) {
      prevAirlineRef.current = airline;
      filterBarRef.current?.reset();
      handleReset();
    }
  }, [airline, handleReset]);

  // Fetch aircraft list for autocomplete
  useEffect(() => {
    if (!airline || !accessToken) return;

    let isMounted = true;

    const loadAircraftList = async () => {
      try {
        const list = await fetchAirlineByAirline(airline, accessToken);
        if (isMounted) {
          setAircraftList(Array.isArray(list) ? list : []);
        }
      } catch {
        if (isMounted) {
          setAircraftList([]);
        }
      }
    };

    void loadAircraftList();

    return () => {
      isMounted = false;
    };
  }, [airline, accessToken]);

  const handleSearch = useCallback(async (criteria: LogtSearchCriteria) => {
    if (!accessToken) return;

    // Cancel any in-flight search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setData([]);
    setPage(1);
    setSelectedRow(null);
    setMessageContent(null);
    setLastCriteria(criteria);
    setLastRefreshed(new Date());

    try {
      const result = await searchLogTs(1, limit, { ...criteria, Page: 1, Limit: limit, Airline: airline }, accessToken, useExtendedSearchApi, controller.signal);
      const records = result.logs || [];
      const total = result.totalCount || 0;
      setData(records);
      setTotalCount(total);
      setHasMore(records.length < total);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED' || err.name === 'AbortError') {
        // Search was cancelled by user — don't update state
        return;
      }
      console.error('Beta search error:', err);
      setData([]);
      setTotalCount(0);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [accessToken, airline, limit, useExtendedSearchApi]);

  const handleLoadMore = useCallback(async () => {
    if (!lastCriteria || !accessToken || isLoading) return;
    const nextPage = page + 1;
    setIsLoading(true);

    try {
      const result = await searchLogTs(nextPage, limit, { ...lastCriteria, Page: nextPage, Limit: limit, Airline: airline }, accessToken, useExtendedSearchApi);
      const records = result.logs || [];
      setData(prev => [...prev, ...records]);
      setPage(nextPage);
      setHasMore(data.length + records.length < totalCount);
    } catch (err) {
      console.error('Beta load more error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [lastCriteria, accessToken, isLoading, page, limit, airline, data.length, totalCount, useExtendedSearchApi]);

  const handleSort = useCallback((column: string) => {
    const newDirection = sortColumn === column && sortDirection === 'DESC' ? 'ASC' : 'DESC';
    setSortColumn(column);
    setSortDirection(newDirection);

    if (lastCriteria && accessToken) {
      const updatedCriteria = { ...lastCriteria, SortColumn: column, SortDirection: newDirection, Page: 1, Limit: limit };
      setLastCriteria(updatedCriteria);
      setIsLoading(true);
      setData([]);
      setPage(1);

      searchLogTs(1, limit, { ...updatedCriteria, Airline: airline }, accessToken, useExtendedSearchApi)
        .then((result) => {
          const records = result.logs || [];
          const total = result.totalCount || 0;
          setData(records);
          setTotalCount(total);
          setHasMore(records.length < total);
        })
        .catch(() => { setData([]); setTotalCount(0); })
        .finally(() => setIsLoading(false));
    }
  }, [sortColumn, sortDirection, lastCriteria, accessToken, limit, airline, useExtendedSearchApi]);

  const handleRowSelect = useCallback(async (row: ILogt) => {
    if (selectedRow?.logtId === row.logtId) {
      setSelectedRow(null);
      setMessageContent(null);
      return;
    }
    setSelectedRow(row);
    setMessageContent(null);
    setMessageError(false);
    setMessageLoading(true);

    try {
      const content = await fetchLogTContentById(row.logtId, accessToken, useExtendedSearchApi);
      setMessageContent(content);
    } catch {
      setMessageError(true);
    } finally {
      setMessageLoading(false);
    }
  }, [selectedRow, accessToken, useExtendedSearchApi]);

  return (
    <div className="beta-content">
      {/* Vertical Filter Sidebar */}
      <BetaFilterBar
        ref={filterBarRef}
        onSearch={handleSearch}
        onReset={handleReset}
        onFiltersChange={setActiveFilters}
        isLoading={isLoading}
        airline={airline}
        aircraftList={aircraftList}
        accessToken={accessToken}
        loadedMessageTypes={data.length > 0 ? [...new Set(data.map(d => (d as any).MessageType || (d as any).messageType).filter(Boolean))] : undefined}
      />

      {/* Main content area */}
      <div className="beta-content-main">
        {/* Toolbar */}
        <div className="beta-toolbar">
          <div className="beta-toolbar-row-top">
            <span className="beta-toolbar-count">
              {totalCount > 0
                ? `${(data.length > totalCount ? totalCount : data.length).toLocaleString()} of ${totalCount.toLocaleString()} records`
                : 'Use filters to search'}
              {hasMore && ' · scroll for more'}
              {isLoading && <span className="beta-grid-loading-text" style={{ marginLeft: '12px' }}>⟳ Loading…</span>}
            </span>
            <div className="beta-toolbar-actions">
              {data.length > 0 && (
                <BetaExportButtons data={data} />
              )}
              {lastRefreshed && (
                <span className="beta-toolbar-refreshed">
                  Last refreshed: {formatZuluDateTimeLabel(lastRefreshed)}
                </span>
              )}
            </div>
          </div>
          <div className="beta-toolbar-row-bottom">
            {activeFilters && (activeFilters.tailNumbers.length > 0 || activeFilters.flightNumbers.length > 0 || activeFilters.messageTypes.length > 0 || activeFilters.keyword || activeFilters.smi || activeFilters.orig || activeFilters.dest) && (
              <div className="beta-active-filters">
                {activeFilters.tailNumbers.map(t => (
                  <span key={`tail-${t}`} className="beta-active-filter-badge"><strong>Aircraft</strong> {t}
                    <button type="button" className="beta-badge-close" aria-label={`Remove aircraft ${t}`} onClick={() => filterBarRef.current?.removeFilter('tail', t)}>×</button>
                  </span>
                ))}
                {activeFilters.flightNumbers.map(f => (
                  <span key={`flight-${f}`} className="beta-active-filter-badge"><strong>Flight</strong> {f}
                    <button type="button" className="beta-badge-close" aria-label={`Remove flight ${f}`} onClick={() => filterBarRef.current?.removeFilter('flight', f)}>×</button>
                  </span>
                ))}
                {activeFilters.messageTypes.map(m => (
                  <span key={`msg-${m.Value}`} className="beta-active-filter-badge"><strong>Msg Type</strong> {m.Value}
                    <button type="button" className="beta-badge-close" aria-label={`Remove msg type ${m.Value}`} onClick={() => filterBarRef.current?.removeFilter('msgType', m.Value)}>×</button>
                  </span>
                ))}
                {activeFilters.keyword && (
                  <span className="beta-active-filter-badge"><strong>Keyword</strong> {activeFilters.keyword}
                    <button type="button" className="beta-badge-close" aria-label="Remove keyword" onClick={() => filterBarRef.current?.removeFilter('keyword')}>×</button>
                  </span>
                )}
                {activeFilters.smi && (
                  <span className="beta-active-filter-badge"><strong>SMI</strong> {activeFilters.smi}
                    <button type="button" className="beta-badge-close" aria-label="Remove smi" onClick={() => filterBarRef.current?.removeFilter('smi')}>×</button>
                  </span>
                )}
                {activeFilters.orig && (
                  <span className="beta-active-filter-badge"><strong>Orig</strong> {activeFilters.orig}
                    <button type="button" className="beta-badge-close" aria-label="Remove orig" onClick={() => filterBarRef.current?.removeFilter('orig')}>×</button>
                  </span>
                )}
                {activeFilters.dest && (
                  <span className="beta-active-filter-badge"><strong>Dest</strong> {activeFilters.dest}
                    <button type="button" className="beta-badge-close" aria-label="Remove dest" onClick={() => filterBarRef.current?.removeFilter('dest')}>×</button>
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              className="beta-toolbar-userhistory-btn"
              onClick={() => setShowCsvModal(true)}
            >
              Users History Report
            </button>
          </div>
        </div>

      {/* Data grid */}
      <BetaLogTGrid
        data={data}
        totalCount={totalCount}
        isLoading={isLoading}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        selectedId={selectedRow?.logtId || null}
        onRowSelect={handleRowSelect}
        onCancelSearch={handleCancelSearch}
      />

      {/* Message detail panel */}
      {selectedRow && (
        <BetaMessageDetail
          row={selectedRow}
          content={messageContent}
          isLoading={messageLoading}
          hasError={messageError}
          onClose={() => {
            setSelectedRow(null);
            setMessageContent(null);
          }}
        />
      )}

      {/* Users History CSV Modal */}
      {showCsvModal && (
        <OverviewCsvModal
          showCsvModal={showCsvModal}
          setShowCsvModal={setShowCsvModal}
          csvStartDate={csvStartDate}
          setCsvStartDate={setCsvStartDate}
          csvEndDate={csvEndDate}
          setCsvEndDate={setCsvEndDate}
          reportType={csvReportType}
          setReportType={setCsvReportType}
          handleCsvDownload={handleCsvDownload}
          isCSVLoading={isCSVLoading}
        />
      )}
      </div>
    </div>
  );
};

export default BetaOverview;
